import Button from '@mui/material/Button';
import { useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowBack, CheckCircle, Close } from "@mui/icons-material";
import { ProfileResponse } from "@/utils/Hooks";
import { ProfileData } from "@/types/shared.types";
import { KeyedMutator } from "swr";
import MessageContext from '@/context/MessageProvider';

const EntryModalPages = [
  {
    title: "Tujuan",
    modal: TujuanPage
  },
  {
    title: "Loker",
    modal: LokerPage
  },
  {
    title: "Tas",
    modal: BagPage
  },
  {
    title: "Akhir",
    modal: ReviewPage
  },
  {
    title: "Selesai",
    modal: SelesaiPage,
    noReturn: true,
    excludeOnStages: true
  }
];

type KunjunganData = {
  lockerNumber?: string;
  bagNumber?: string;
}

type EntryModalProps = {
  accessToken: string,
  setOpen: (open: boolean) => void,
  profile?: { data: ProfileData | undefined; mutate: KeyedMutator<ProfileResponse> | undefined } | undefined

  visitorToken: string,
  handleRootBack: () => void;
}

export default function EntryModal({ accessToken, setOpen, profile, visitorToken, handleRootBack }: EntryModalProps) {
  const [page, setPage] = useState(0);
  const handleClose = () => setOpen(false);

  const [purpose, setPurpose] = useState<string | null>(null);
  const [usingLocker, setUsingLocker] = useState<boolean>(false);
  const [usingBag, setUsingBag] = useState<boolean>(false);
  const [kunjunganData, setKunjunganData] = useState<KunjunganData | null>(null);

  const goToNextPage = () => {
    if (page === EntryModalPages.length - 1) {
      handleClose();
      return;
    }
    setPage(page + 1);
  };

  const goToPrevPage = () => {
    if (page === 0) {
      handleRootBack();
      return;
    }
    setPage(page - 1);
  };

  return <>
    <Box sx={
      {
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        marginBottom: 1
      }
    }>
      {EntryModalPages.map(({ title, modal, excludeOnStages }, index) => {
        if (excludeOnStages) {
          return null;
        }
        return <Box
          sx={{ flexGrow: 1 }}
          key={index}
        >
          <Box
            sx={{
              borderBottom: "2px solid",
              borderBottomColor: index <= page ? "primary.main" : "rgba(0, 0, 0, 0.20)"
            }}
          ></Box>
          <Typography>{title}</Typography>
        </Box>;
      })}
      <Button
        sx={{
          flexShrink: 1,
          minWidth: 0,
          padding: 0
        }}
        onClick={handleClose}>
        <Close/>
      </Button>
    </Box>

    {(!EntryModalPages[page].noReturn) && <Button
      variant="outlined"
      onClick={goToPrevPage}
      size="small"
      startIcon={<ArrowBack/>}
      sx={{
        marginBottom: 1
      }}
    >
      Kembali
    </Button>
    }
    {EntryModalPages.map(({ title, modal }, index) => {
      if (index !== page) {
        return null;
      }
      const Page = modal;
      return <Page
        key={title}
        accessToken={accessToken}
        visitorToken={visitorToken}
        purpose={purpose || ""}
        setPurpose={setPurpose}
        usingLocker={usingLocker}
        setUsingLocker={setUsingLocker}
        usingBag={usingBag}
        setUsingBag={setUsingBag}
        kunjunganData={kunjunganData || { lockerNumber: "", bagNumber: "" }}
        setKunjunganData={setKunjunganData}
        profile={profile}
        nextCallback={goToNextPage}
        prevCallback={goToPrevPage}
      />;
    })}
  </>;
}

type EntryModalPageProps = {
  key?: any;

  nextCallback: () => void;
  prevCallback: () => void;
  accessToken: string;
  profile?: { data: ProfileData | undefined; mutate: KeyedMutator<ProfileResponse> | undefined } | undefined

  visitorToken: string;

  purpose: string;
  setPurpose: (purpose: string) => void;

  usingLocker: boolean;
  setUsingLocker: (usingLocker: boolean) => void;

  usingBag: boolean;
  setUsingBag: (usingBag: boolean) => void;

  kunjunganData: KunjunganData;
  setKunjunganData: (kunjunganData: KunjunganData) => void;
}

const reasons = [
  {
    id: 'MEMBACA',
    name: 'Membaca'
  },
  {
    id: 'MEMINJAM_MENGEMBALIKAN',
    name: 'Meminjam/Mengembalikan'
  },
  {
    id: 'BELAJAR_DISKUSI',
    name: 'Belajar/Diskusi'
  },
  {
    id: 'LAINNYA',
    name: 'Lainnya'
  }
];

function TujuanPage({ nextCallback, prevCallback, accessToken, purpose, setPurpose, key }: EntryModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Apa alasan Anda berkunjung ke perpustakaan?</Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      {reasons.map(reason => {
        return <Button
          key={reason.id}
          variant="contained"
          onClick={() => {
            setPurpose(reason.id);
            nextCallback();
          }}
          sx={{
            width: '100%',
            textAlign: 'left'
          }}
          size="large"
        >
          {reason.name}
        </Button>;
      })}
    </Box>
  </Box>;
}

function LokerPage({ nextCallback, setUsingLocker, key }: EntryModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Apakah Anda ingin meminjam loker?</Typography>

    <Typography>
      Anda dapat menggunakan loker untuk menyimpan barang yang tidak dapat dibawa ke dalam Perpustakaan STIS.
    </Typography>

    <Typography>
      Anda akan mengambil kunci untuk loker yang akan Anda gunakan. Jika kunci ini hilang, Anda akan diberi sanksi
      tertentu.
    </Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Button
        variant="contained"
        onClick={() => {
          setUsingLocker(true);
          nextCallback();
        }}
        sx={{
          width: '100%',
          textAlign: 'left'
        }}
        size="large"
      >
        Ya
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          setUsingLocker(false);
          nextCallback();
        }}
        sx={{
          width: '100%',
          textAlign: 'left'
        }}
        size="large"
      >
        Tidak
      </Button>
    </Box>
  </Box>;
}

function BagPage({ nextCallback, setUsingBag, key }: EntryModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Apakah Anda ingin meminjam tas?</Typography>

    <Typography>
      Anda dapat meminjam tas transparan, namun tas tersebut harus disimpan dengan baik dan dikembalikan setelah meninggalkan perpustakaan.
    </Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Button
        variant="contained"
        onClick={() => {
          setUsingBag(true);
          nextCallback();
        }}
        sx={{
          width: '100%',
          textAlign: 'left'
        }}
        size="large"
      >
        Ya
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          setUsingBag(false);
          nextCallback();
        }}
        sx={{
          width: '100%',
          textAlign: 'left'
        }}
        size="large"
      >
        Tidak
      </Button>
    </Box>
  </Box>;
}


function ReviewPage(
  {
    nextCallback,
    accessToken,
    visitorToken,
    purpose,
    usingLocker,
    usingBag,
    setKunjunganData,
    profile,
    key
  }: EntryModalPageProps) {
  const [loading, setLoading] = useState(false);
  const { setMessage } = useContext(MessageContext)!;

  const submitVisitor = async () => {
    setLoading(true);

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/visitor/enter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        token: visitorToken,
        purpose,
        usingLocker,
        usingBag
      })
    });

    setLoading(false);

    const responseJson = await response.json();

    if (!response.ok) {
      setMessage({ isOpen: true, content: responseJson.message ?? "Terjadi sebuah kesalahan. Silahkan coba lagi nanti", severity: "error" });
      return;
    }

    setKunjunganData(responseJson.data);
    if (profile && profile.data && profile.mutate) {
      profile.mutate({
        data: {
          ...profile.data,
          kunjungan: {
            waktuMasuk: new Date().toISOString(),
            loker: responseJson?.data?.lockerNumber,
            tas: responseJson?.data?.bagNumber
          }
        }
      });
    }
    nextCallback();

  };

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Review kembali</Typography>

    <Typography>
      Anda berkunjung dengan rincian berikut.
    </Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Alasan kunjungan</Typography>
      <Typography>{reasons.find(reason => reason.id === purpose)?.name}</Typography>
    </Box>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman loker</Typography>
      <Typography>{usingLocker ? 'Ya' : 'Tidak'}</Typography>
    </Box>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Membawa tas</Typography>
      <Typography>{usingBag ? 'Ya' : 'Tidak'}</Typography>
    </Box>

    <Typography>
      Dengan berkunjung ke Perpustakaan STIS, Anda setuju untuk mengikuti tata tertib dan aturan yang
      berlaku
    </Typography>

    <Button
      variant="contained"
      onClick={submitVisitor}
      disabled={loading}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Kirim
    </Button>
  </Box>;
}

function SelesaiPage({ nextCallback, kunjunganData, key }: EntryModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      alignItems: 'center'
    }}>
      <CheckCircle sx={{
        width: 96,
        height: 96
      }}/>

      <Typography component="h2" sx={{
        fontSize: 20,
        fontWeight: 700
      }}>Kunjungan terdaftar</Typography>
    </Box>
    <Typography>
      <b>Kunjungan Anda telah terdaftar.</b> Jangan lupa untuk akhiri kunjungan Anda di SIMPus saat Anda selesai
      berkunjung.
    </Typography>

    {kunjunganData?.lockerNumber && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman loker</Typography>
      <Typography>Nomor loker Anda adalah sebagai berikut.</Typography>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{
          fontSize: 28,
          fontWeight: 700,
          borderRadius: "100%",
          backgroundColor: "primary.main",
          color: "white",
          width: 80,
          height: 80,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {kunjunganData?.lockerNumber}
        </Box>
      </Box>
    </Box>
    }

    {kunjunganData?.bagNumber && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman tas</Typography>
      <Typography>Nomor tas Anda adalah sebagai berikut.</Typography>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box sx={{
          fontSize: 28,
          fontWeight: 700,
          borderRadius: "100%",
          backgroundColor: "secondary.main",
          color: "white",
          width: 80,
          height: 80,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {kunjunganData?.bagNumber}
        </Box>
      </Box>
    </Box>
    }

    <Button
      variant="contained"
      onClick={nextCallback}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Selesai
    </Button>
  </Box>;
}